import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Card,
  CardBody,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Switch,
} from '@nextui-org/react';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  FileText,
  Plus,
  Trash2,
  XCircle,
} from 'lucide-react';
import adminPagesService from '../../services/adminPagesService';
import { DynamicPage } from '../../services/dynamicPageService';

const ACCENT = '#3fced1';

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '–';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '–';
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const FeedbackBanner: React.FC<{
  message: string | null;
  type: 'success' | 'error';
  onClose: () => void;
}> = ({ message, type, onClose }) => {
  if (!message) return null;
  const isSuccess = type === 'success';
  return (
    <div
      className={`mb-4 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
        isSuccess
          ? 'border-[#3fced1]/40 bg-[#3fced1]/10 text-[#3fced1]'
          : 'border-red-500/40 bg-red-500/10 text-red-400'
      }`}
    >
      {isSuccess ? <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> : <XCircle size={18} className="mt-0.5 shrink-0" />}
      <span className="flex-1">{message}</span>
      <button type="button" onClick={onClose} aria-label="Hinweis schließen" className="opacity-70 hover:opacity-100">✕</button>
    </div>
  );
};

const AdminPagesPage: React.FC = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState<DynamicPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await adminPagesService.listPages();
      setPages(list);
    } catch (err) {
      setError(adminPagesService.getErrorMessage(err, 'Seiten konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleTitleChange = (value: string) => {
    setNewTitle(value);
    if (!slugTouched) setNewSlug(adminPagesService.slugify(value));
  };

  const handleCreate = async () => {
    if (!newTitle.trim() || !newSlug.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const page = await adminPagesService.createPage({ title: newTitle, slug: newSlug });
      setCreateOpen(false);
      setNewTitle('');
      setNewSlug('');
      setSlugTouched(false);
      if (page?.id) {
        navigate(`/admin/seiten/${page.id}`);
      } else {
        await load();
      }
    } catch (err) {
      setError(adminPagesService.getErrorMessage(err, 'Seite konnte nicht angelegt werden.'));
    } finally {
      setCreating(false);
    }
  };

  const handleToggleVisibility = async (page: DynamicPage) => {
    // Optimistisch umschalten, bei Fehler zurückrollen
    setPages((prev) => prev.map((p) => (p.id === page.id ? { ...p, isVisible: !p.isVisible } : p)));
    try {
      await adminPagesService.toggleVisibility(page.id);
    } catch (err) {
      setPages((prev) => prev.map((p) => (p.id === page.id ? { ...p, isVisible: page.isVisible } : p)));
      setError(adminPagesService.getErrorMessage(err, 'Sichtbarkeit konnte nicht geändert werden.'));
    }
  };

  const handleDelete = async (page: DynamicPage) => {
    if (confirmDeleteId !== page.id) {
      setConfirmDeleteId(page.id);
      return;
    }
    setConfirmDeleteId(null);
    try {
      await adminPagesService.deletePage(page.id);
      setPages((prev) => prev.filter((p) => p.id !== page.id));
      setFeedback(`"${page.title}" gelöscht.`);
    } catch (err) {
      setError(adminPagesService.getErrorMessage(err, 'Seite konnte nicht gelöscht werden.'));
    }
  };

  return (
    <>
      <Helmet>
        <title>Seiten | Admin | DartCloud</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex items-center gap-3">
          <Button
            as={RouterLink}
            to="/admin"
            isIconOnly
            variant="bordered"
            className="border-zinc-700 text-gray-300"
            aria-label="Zurück zum Admin-Dashboard"
          >
            <ArrowLeft size={18} />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="flex items-center gap-2 text-2xl font-extrabold tracking-tight text-white">
              <FileText size={22} style={{ color: ACCENT }} />
              Seiten
            </h1>
            <p className="text-sm text-gray-400">
              CMS-Seiten wie Impressum & Datenschutz bearbeiten oder neue Seiten anlegen.
            </p>
          </div>
          <Button
            className="bg-[#3fced1] font-semibold text-black"
            startContent={<Plus size={16} />}
            onPress={() => setCreateOpen(true)}
          >
            Neue Seite
          </Button>
        </div>

        <FeedbackBanner message={feedback} type="success" onClose={() => setFeedback(null)} />
        <FeedbackBanner message={error} type="error" onClose={() => setError(null)} />

        {loading ? (
          <div className="flex min-h-[300px] items-center justify-center">
            <Spinner size="lg" color="primary" />
          </div>
        ) : (
          <div className="space-y-3">
            {pages.map((page) => (
              <Card key={page.id} className="border border-zinc-800 bg-zinc-900">
                <CardBody className="flex flex-row flex-wrap items-center gap-3 p-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/seiten/${page.id}`)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#3fced1]/10 text-[#3fced1]">
                      <FileText size={18} />
                    </span>
                    <span className="min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-base font-bold text-white">{page.title}</span>
                        {!page.isVisible && (
                          <span className="shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold text-amber-400">
                            Unsichtbar
                          </span>
                        )}
                      </span>
                      <span className="block truncate text-xs text-gray-500">
                        /dartcloud/{page.slug} · {page.contentBlocks.length} Blöcke · Aktualisiert: {formatDateTime(page.updatedAt)}
                      </span>
                    </span>
                  </button>

                  <div className="flex shrink-0 items-center gap-2">
                    <Switch
                      size="sm"
                      isSelected={page.isVisible}
                      onValueChange={() => handleToggleVisibility(page)}
                      aria-label="Sichtbar"
                    />
                    <Button
                      as="a"
                      href={`/dartcloud/${page.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      isIconOnly
                      size="sm"
                      variant="light"
                      aria-label="Seite ansehen"
                      className="text-gray-400"
                    >
                      <ExternalLink size={15} />
                    </Button>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      aria-label="Seite löschen"
                      className={confirmDeleteId === page.id ? 'text-red-400' : 'text-gray-500 hover:text-red-400'}
                      onPress={() => handleDelete(page)}
                    >
                      <Trash2 size={15} />
                    </Button>
                    <ChevronRight size={16} className="text-gray-600" />
                  </div>
                  {confirmDeleteId === page.id && (
                    <p className="w-full text-xs text-red-400">
                      Wirklich löschen? Erneut auf den Papierkorb klicken zum Bestätigen.
                    </p>
                  )}
                </CardBody>
              </Card>
            ))}

            {pages.length === 0 && (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <FileText size={40} className="text-zinc-700" />
                <p className="text-gray-500">Noch keine Seiten. Lege die erste an!</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Anlegen-Modal */}
      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} placement="center">
        <ModalContent className="border border-zinc-800 bg-zinc-900">
          <ModalHeader className="text-white">Neue Seite</ModalHeader>
          <ModalBody>
            <Input
              autoFocus
              label="Titel"
              placeholder='z.B. "Impressum"'
              value={newTitle}
              onValueChange={handleTitleChange}
              classNames={{ inputWrapper: 'bg-zinc-800 border border-zinc-700', input: 'text-white', label: 'text-gray-400' }}
            />
            <Input
              label="Slug (URL)"
              placeholder="impressum"
              value={newSlug}
              onValueChange={(v) => {
                setSlugTouched(true);
                setNewSlug(v);
              }}
              description={`Erreichbar unter /dartcloud/${newSlug || '…'}`}
              classNames={{ inputWrapper: 'bg-zinc-800 border border-zinc-700', input: 'text-white', label: 'text-gray-400' }}
            />
            <p className="text-xs text-gray-500">
              Die Seite startet <strong>unsichtbar</strong> — Inhalte fügst du im nächsten Schritt
              als Blöcke hinzu und schaltest sie dann sichtbar.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setCreateOpen(false)}>Abbrechen</Button>
            <Button
              isLoading={creating}
              isDisabled={!newTitle.trim() || !newSlug.trim()}
              className="bg-[#3fced1] font-semibold text-black"
              onPress={handleCreate}
            >
              Anlegen
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AdminPagesPage;
